import { createAmBeApiStore } from "@timeedit/schedule-fe-lib/am-be";
import { EAppEnv } from "@timeedit/types/lib/enums/appEnv.enum";
import { ERegion } from "@timeedit/types/lib/enums/region.enum";
import { getAccessToken } from "@timeedit/ui-components/components/AuthWrapper/AuthWrapper";
import { App } from "../models/App";
import Log from "./Log";
import Language from "./Language";

const regionSubdomains: Record<ERegion, string> = {
    [ERegion.EU_EES]: "eu",
    [ERegion.ANZ]: "anz",
    [ERegion.SGP]: "sgp",
    [ERegion.UK]: "uk",
};

// This is currently stored in all am-be consumer client fe apps in this way.
const amBeDomains: Record<Exclude<EAppEnv, EAppEnv.DEVELOPMENT>, string> = {
    [EAppEnv.BETA]: "timeedit.dev",
    [EAppEnv.TEST]: "test.timeedit.net",
    [EAppEnv.STAGING]: "timeedit.io",
    [EAppEnv.PRODUCTION]: "timeedit.net",
};

const isAppEnv = (value: string): value is EAppEnv =>
    Object.values(EAppEnv).includes(value as EAppEnv);

const isRegion = (value: string): value is ERegion =>
    Object.values(ERegion).includes(value as ERegion);

const getAmBeUrl = (
    env: string,
    region: string | undefined,
    developmentUrl = "http://localhost:3011"
): string => {
    if (!isAppEnv(env)) {
        throw new Error(`Unsupported environment: ${env}`);
    }
    if (env === EAppEnv.DEVELOPMENT) {
        return `${developmentUrl}/v1/`;
    }
    if (!region || !isRegion(region)) {
        throw new Error(`Unsupported region: ${region}`);
    }
    return `https://activitymanager.${regionSubdomains[region]}.${amBeDomains[env]}/v1/`;
};

/**
 * The store for the am-be API cache, shared with te-prefs-lib (am-fe).
 */
export const createTecoreAmBeApiStore = (getApp: () => App) =>
    createAmBeApiStore({
        getBaseUrl: () => {
            if (process.env.NODE_ENV === "development") {
                return getAmBeUrl(
                    EAppEnv.DEVELOPMENT,
                    undefined,
                    process.env.AM_BE_URL || undefined
                );
            }
            const env = getApp().env;
            return getAmBeUrl(env?.serverEnv ?? "", env?.serverRegion);
        },
        getAccessToken,
        onError: (error, endpoint) =>
            Log.error(Language.get("nc_am_be_request_failed"), {
                details: [`am-be ${endpoint} failed with status ${error.status}`],
            }),
    });
